import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isCatalystTier } from '@/lib/features/featureFlags';
import { pushClientError } from '@/utils/clientErrorBuffer';
import { clock } from '@/utils/clock';
// Core-100 R4 wave 3 (CORE-BE B): the one chain-membership test (sequence_index defaults to 1; generation 1 alone is
// not a chain — this copy treated it as one and silenced the monitor for every ticket created without the column).
import { isPartOfRolloverChain } from '@/utils/rolloverChainMembership';
// Phase 10.3: rollover-promise planner (Phase 8.1) preflight. Advisory context for the person deciding.
import {
  getRolloverPromiseResolutionPlan,
  type RolloverPromiseTicketShape,
} from '@/utils/rolloverPromiseResolution';
// Phase 10.4: run-long EOD continuation planner (Phase 8.2). Advisory context for the person deciding.
import {
  getRunLongEODContinuationPlan,
  type RunLongEODContinuationInput,
} from '@/utils/runLongEODContinuation';
// OCT28 WP-2b-2b-ii-b2-A (founder decision, OPTION B): the database's end-of-day judgement, not a browser estimate.
import {
  claimEodDecisionShown,
  clearEodPendingVerification,
  loadServerEodVerdict,
  useEodPendingVerification,
  useScheduleFreshness,
  type ServerEodVerdict,
} from '@/lib/scheduling/serverEodState';

interface UseEODMonitorProps {
  ticketId: string;
  ticketStatus: string;
  actuallyStarted: boolean;
  timerStatus: string;
  /** The committed finish (tickets.opti_finish): a change re-reads the database's judgement. */
  optiFinish?: string | null;
  techId: string | undefined;
  customerName: string;
  shopTier?: string;
  shopId?: string;
}

/**
 * How a CONFLICT reaches a person:
 *   modal                  the existing EOD modal (`eod-exceeded`) — a plain overrun with no decision recorded yet
 *   proposal_pending       a rollover proposal already exists for it: the decision is waiting, not re-asked
 *   chain_unrepresentable  a rollover-chain row that still overruns: an authorized continuation would have capped it,
 *                          so this is a genuine outstanding decision — but the conversion modal cannot represent a
 *                          chain row, so it is shown on the card as unresolved instead (never suppressed, never
 *                          approved here)
 */
export type EodPresentation = 'modal' | 'proposal_pending' | 'chain_unrepresentable';
export interface EodMonitorResult { verdict: ServerEodVerdict; presentation: EodPresentation | null }

const TERMINAL = new Set(['Completed', 'Cancelled', 'Deleted']);

/**
 * Presents the database's end-of-day judgement (ticket_eod_status) for a paused ticket — and for a ticket resumed while
 * that judgement was not confirmed (founder decision, Option a).
 *
 * OCT28 WP-2b-2b-ii-b2-A: it no longer derives a finish (start + OptiTime + breaks + "wall time − worked") against a
 * flat shift end — that counted parked hours, nights and breaks as pause, ignored dated hours and authorized overtime,
 * and disagreed with the server. Re-read when the shared reconcile freshness changes (about once a minute while the
 * shop has live work), when the committed finish changes, or on the board's `ticket-updated` event — never per tick.
 * Each read is scoped to its shop + ticket + request: an older answer never replaces a newer one.
 */
export function useEODMonitor({
  ticketId,
  ticketStatus,
  actuallyStarted,
  timerStatus,
  optiFinish,
  techId,
  customerName,
  shopTier,
  shopId,
}: UseEODMonitorProps): EodMonitorResult | null {
  const [result, setResult] = useState<EodMonitorResult | null>(null);
  const [bump, setBump] = useState(0);
  const runRef = useRef(0);
  const freshness = useScheduleFreshness(shopId);
  const pending = useEodPendingVerification(shopId, ticketId);

  // STAGE 0B: "the ticket is paused" is the timer topology — no session open and unresolved pause(s) — which Timer V2
  // reports as `timer_status = 'paused'` while leaving `status = 'Active'`. The legacy status is still honoured.
  const isFullyPaused = ticketStatus === 'Paused' || timerStatus === 'paused';
  const terminal = TERMINAL.has(ticketStatus);
  const monitored = !terminal && actuallyStarted && !!techId && !!shopId && (isFullyPaused || !!pending);
  const freshKey = freshness
    ? `${freshness.pending}|${freshness.stale}|${freshness.failing}|${freshness.lastReconciledAt?.getTime() ?? ''}`
    : 'none';

  // the tab-local verification flag ends with the ticket's life or its assignment (never carried to another context)
  useEffect(() => {
    if (!shopId || !pending) return;
    if (terminal || (pending.techId && techId && pending.techId !== techId)) clearEodPendingVerification(shopId, ticketId);
  }, [shopId, ticketId, techId, terminal, pending]);

  // an overtime grant / revocation or another device's change arrives as the board's invalidation
  useEffect(() => {
    if (!monitored) return;
    const onUpdate = () => setBump((n) => n + 1);
    window.addEventListener('ticket-updated', onUpdate);
    return () => window.removeEventListener('ticket-updated', onUpdate);
  }, [monitored]);

  useEffect(() => {
    if (!monitored || !shopId || !techId) { runRef.current += 1; setResult(null); return; }
    const run = ++runRef.current;
    const current = () => run === runRef.current;
    void (async () => {
      try {
        const { data: existingTicket } = await supabase
          .from('tickets')
          .select('id, parent_ticket_id, sequence_index, split_reason, rollover_of_ticket_id, type, due_out, scheduled_start_time, status, picked_up_at, archived_at, deleted_at')
          .eq('id', ticketId)
          .single();
        if (!current()) return;
        const { data: tech } = await supabase.from('technicians').select('id, name').eq('id', techId).single();
        const { verdict } = await loadServerEodVerdict(ticketId, shopId);
        if (!current()) return;
        if (verdict.state !== 'conflict') {
          setResult({ verdict, presentation: null });
          if (verdict.state === 'clear') clearEodPendingVerification(shopId, ticketId);
          return;
        }
        // a confirmed conflict is NOT a resolution: the ticket stays under verification (still monitored while running,
        // still visible on the card, the same decision not re-raised) until the server answers NONE, or the ticket ends
        // or is reassigned
        const presentation: EodPresentation = verdict.conversionId ? 'proposal_pending'
          : isPartOfRolloverChain(existingTicket) ? 'chain_unrepresentable' : 'modal';
        setResult({ verdict, presentation });
        if (presentation !== 'modal' || !claimEodDecisionShown(shopId, ticketId, verdict.decisionKey)) return;

        const monitoredTicketType = (existingTicket as { type?: string | null } | null)?.type ?? null;
        const committed = verdict.committedFinish ? new Date(verdict.committedFinish) : clock.now();
        const boundary = verdict.boundaryAt ? new Date(verdict.boundaryAt) : null;
        const { data: prof } = await supabase.from('shop_profile').select('time_zone').eq('shop_id', shopId).maybeSingle();
        if (!current()) return;
        const tz = (prof as { time_zone?: string | null } | null)?.time_zone ?? null;
        const techEOD = boundary && tz
          ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }).format(boundary)
          : null;
        // Phase 10.3 / 10.4 planners stay ADVISORY context for the person deciding (W4: no silent auto-rollover on any
        // tier). They read the database's committed finish and boundary, not a browser estimate.
        const rolloverPlan = getRolloverPromiseResolutionPlan({
          parent: existingTicket as unknown as RolloverPromiseTicketShape,
          projectedPartBFinishAt: committed,
          actorRole: null,
        });
        const runLongInput: RunLongEODContinuationInput = {
          parent: existingTicket as unknown as RolloverPromiseTicketShape,
          now: clock.now(),
          intent: 'stop_and_rollover',
          shiftEnd: boundary ?? committed,
          technicianEnteredRemainingMin: null,
          systemEstimatedRemainingMin: verdict.overrunMin > 0 ? verdict.overrunMin : null,
          projectedPartBFinishAt: committed,
          nextValidWindowStartAt: new Date(clock.nowMs() + 16 * 60 * 60_000),
          actorRole: null,
        };
        const runLongPlan = getRunLongEODContinuationPlan(runLongInput);
        const SAFE_RUN_LONG_VERDICTS = new Set<string>(['create_part_b_continuation', 'uses_system_estimate_fallback', 'continue_past_eod_safe']);
        const plannerAllowsAutoRollover = rolloverPlan.state === 'safe';
        const runLongAllowsAutoRollover = SAFE_RUN_LONG_VERDICTS.has(runLongPlan.verdict);
        window.dispatchEvent(new CustomEvent('eod-exceeded', {
          detail: {
            ticketId,
            customerName,
            techName: (tech as { name?: string } | null)?.name ?? null,
            techEOD,
            rolloverMinutes: verdict.overrunMin,
            trigger: 'pause_monitor',
            source: 'server_eod_status',
            decisionKey: verdict.decisionKey,
            ticketType: monitoredTicketType,
            catalystRecommendation: isCatalystTier(shopTier)
              ? {
                  promiseState: rolloverPlan.state,
                  promiseBlockers: rolloverPlan.blockers,
                  runLongVerdict: runLongPlan.verdict,
                  recommendsRollover: monitoredTicketType !== 'Waiter' && plannerAllowsAutoRollover && runLongAllowsAutoRollover,
                }
              : null,
          },
        }));
      } catch (error) {
        // FIX-R132-MED-2: surface the failure; the card shows the check as not confirmed, never as fine.
        const errMsg = (error as { message?: string } | undefined)?.message ?? String(error);
        console.error('[EODMonitor] Error reading the server EOD status:', error);
        if (current()) setResult({ verdict: { state: 'unconfirmed', reason: 'unavailable', lastReconciledAt: freshness?.lastReconciledAt ?? null }, presentation: null });
        pushClientError({
          source: 'mutation',
          message: `[EODMonitor] server EOD status threw: ${errMsg}`,
          stack: (error as { stack?: string } | undefined)?.stack,
          extra: { ticketId },
          shopId: shopId ?? null,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitored, ticketId, techId, shopId, optiFinish, freshKey, bump]);

  return result;
}
